/**
 * Mask an email address by revealing the first N chars of the local part
 * and replacing the rest with a mask (default "***").
 *
 * Examples:
 *  hideEmail("hoanganh@gmail.com")           -> "hoang***@gmail.com"
 *  hideEmail("bob@example.com", { keepStart: 3 }) -> "bob***@example.com"
 */
export function hideEmail(
  email: string,
  options: {
    keepStart?: number // how many characters to show from the start of the local part
    mask?: string // the mask to append
    repeatToLength?: boolean // if true, repeat mask to match hidden length
  } = {}
): string {
  const keepStart = options.keepStart ?? 5
  const mask = options.mask ?? '***'
  const repeatToLength = options.repeatToLength ?? false

  if (typeof email !== 'string') return ''

  const trimmed = email.trim()
  const match = trimmed.match(/^([^@]+)@(.+)$/)
  if (!match) return trimmed // not a valid basic email shape; return as-is

  const [_, local, domain] = match

  // Ensure we always hide at least 1 char if possible
  const visibleCount = Math.max(0, Math.min(keepStart, Math.max(0, local.length - 1)))
  const hiddenCount = Math.max(0, local.length - visibleCount)

  const visible = local.slice(0, visibleCount)
  const masked =
    hiddenCount === 0
      ? ''
      : repeatToLength
        ? mask.repeat(Math.ceil(hiddenCount / mask.length)).slice(0, hiddenCount)
        : mask

  return `${visible}${masked}@${domain}`
}
